import { DOS_FORMAT_STRING, toCaseId, type CaseId } from "@procision-software/mason";
import {
  Button,
  Input,
  IS_APPLE,
  LinkButton,
  Tooltip,
  useAutoFocus,
  useDrawer,
} from "@procision-software/ui";
import {
  BriefcaseMedicalIcon,
  FileSymlinkIcon,
  PersonStandingIcon,
  ReceiptIcon,
  SearchIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { useDebounce } from "use-debounce";
import type { BillingCaseId } from "~/billing/models/case";
import type { BillingClaimId } from "~/billing/models/claim";
import { useAppContext } from "~/components/AppContextProvider";
import { useRouter } from "~/hooks/useRouter";
import { trpc, type RouterOutputs } from "~/utils/trpc";
import { useIntents } from "./useIntents";

type Context = "appointment" | "overview" | "payer" | "staff" | "transaction";

type IntentShelfProps = {
  search: string;
  caseId?: CaseId;
  billingCaseId?: BillingCaseId;
  billingClaimId?: BillingClaimId;
  context: Context;
};
type SearchResult = RouterOutputs["core"]["search"]["query"][number];
const viewModelForMatch = (match: SearchResult) => {
  switch (match.context) {
    case "appointment":
      return {
        key: `appointment:${match.caseId}`,
        category: "Case",
        icon: PersonStandingIcon,
      };
    case "staff":
      return {
        key: `staff:${match.staffId}`,
        category: "Staff",
        icon: BriefcaseMedicalIcon,
      };
    case "transaction":
      return {
        key: `transaction:${match.transactionId}`,
        category: "Payment",
        icon: ReceiptIcon,
      };
  }
};
const keyForMatch = (match: SearchResult) => viewModelForMatch(match).key;
const iconForResult = (match: SearchResult) => viewModelForMatch(match).icon;
const categorizationForMatch = (match: SearchResult) => viewModelForMatch(match).category;
export const IntentShelf: FC<IntentShelfProps> = (props) => {
  const { formatDate } = useAppContext();
  const drawer = useDrawer();
  const [context, setContext] = useState(props.context ?? "overview");
  const [search, setSearch] = useState(props.search);
  const [caseId, setCaseId] = useState(props.caseId);
  const [billingCaseId, setBillingCaseId] = useState(props.billingCaseId);
  const [billingClaimId, setBillingClaimId] = useState(props.billingClaimId);
  const [staffId, setStaffId] = useState<string | undefined>();
  const focusRef = useAutoFocus(true);
  const intentSubjects = useMemo(
    () => ({
      claimId: billingClaimId,
      billingCaseId,
      caseId,
      staffId,
    }),
    [billingClaimId, billingCaseId, caseId, staffId]
  );
  const exclusions = useMemo((): ("payer" | "staff" | "appointment")[] => {
    if (context === "overview") return ["appointment", "payer", "staff"];
    if (context === "appointment") return ["staff" as const, "payer" as const];
    if (context === "staff") return ["appointment" as const, "payer" as const];
    return [];
  }, [context]);
  const [destinations] = useIntents(intentSubjects, exclusions);
  const router = useRouter();
  const freeTextSearchEnabled = search.length >= 2;
  const [debouncedSearchTerm] = useDebounce(search, 500);
  const possibleMatchesTrpc = trpc.core.search.query.useQuery(
    { terms: debouncedSearchTerm },
    { enabled: freeTextSearchEnabled }
  );
  const staffTrpc = trpc.staff.get.useQuery({ id: staffId ?? "" }, { enabled: !!staffId });
  const caseTrpc = trpc.case.context.useQuery({ id: caseId ?? "" }, { enabled: !!caseId });
  const handleSearchResult = useCallback(
    (match: SearchResult) => () => {
      setContext(match.context);
      switch (match.context) {
        case "appointment":
          setCaseId(toCaseId(match.caseId));
          setBillingCaseId(undefined);
          setBillingClaimId(undefined);
          setStaffId(undefined);
          setSearch("");
          break;
        case "staff":
          setStaffId(match.staffId);
          setCaseId(undefined);
          setBillingCaseId(undefined);
          setBillingClaimId(undefined);
          setSearch("");
          break;
        case "transaction":
          void router.push(`/billing/transactions/${match.transactionId}`);
          break;
      }
    },
    [router]
  );
  useEffect(() => {
    switch (context) {
      case "overview":
        return drawer.setTitlebar({
          title: "Search",
        });
      case "appointment":
        return drawer.setTitlebar({
          title: caseTrpc.data?.patient
            ? `${caseTrpc.data.patient.mrn} - ${formatDate(DOS_FORMAT_STRING, caseTrpc.data.surgeryDate)}`
            : "Appointment",
        });
      case "staff":
        return drawer.setTitlebar({
          title: staffTrpc.data
            ? `${staffTrpc.data.firstName} ${staffTrpc.data.lastName}`
            : "Staff",
        });
      case "transaction":
        return drawer.setTitlebar({
          title: "Transaction",
        });
    }
  }, [context, drawer, staffTrpc.data, caseTrpc.data, formatDate]);

  const terms = useMemo(() => {
    if (!freeTextSearchEnabled) return [];
    return search
      .split(" ")
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .map((s) => s.toLowerCase());
  }, [freeTextSearchEnabled, search]);

  const destinationResults = useMemo(
    () =>
      destinations
        .filter(
          (d): d is typeof d & { href: string } =>
            d.href !== false && terms.every((term) => d.label.toLowerCase().includes(term))
        )
        .map((d) => ({
          key: d.key,
          label: d.label,
          leftIcon: FileSymlinkIcon,
          href: d.href,
        })),
    [destinations, terms]
  );

  const searchResults = useMemo(() => {
    if (!freeTextSearchEnabled || !possibleMatchesTrpc.data) return [];
    return possibleMatchesTrpc.data.map((match) => ({
      key: keyForMatch(match),
      label: `${match.label} (${categorizationForMatch(match)})`,
      leftIcon: iconForResult(match),
      onClick: handleSearchResult(match),
    }));
  }, [freeTextSearchEnabled, possibleMatchesTrpc.data, handleSearchResult]);

  const noResults = destinationResults.length === 0 && searchResults.length === 0;

  return (
    <div className="flex h-full w-full flex-col gap-2 sm:w-96">
      <Input
        ref={focusRef}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        leftIcon={SearchIcon}
        placeholder="Search"
      />
      {noResults && (
        <div className="px-3 py-8 text-center text-sm text-secondary">
          {context === "overview" && (
            <>
              {possibleMatchesTrpc.isLoading && freeTextSearchEnabled
                ? "Searching..."
                : search === ""
                  ? "search for appointments, surgeons, etc"
                  : "No results found"}
            </>
          )}
          {context !== "overview" && "No results found"}
        </div>
      )}
      {destinationResults.length > 0 && (
        <>
          <p className="flex items-center justify-center gap-1 text-sm text-tertiary">
            {IS_APPLE ? "⌘" : "Ctrl"}+click to open{" "}
            <FileSymlinkIcon className="size-4" aria-hidden={true} /> links in a new tab
          </p>
          {destinationResults.map((result) => (
            <LinkButton
              key={result.key}
              href={result.href}
              leftIcon={result.leftIcon}
              className="justify-start"
            >
              <span className="truncate">{result.label}</span>
            </LinkButton>
          ))}
        </>
      )}
      {searchResults.length > 0 &&
        searchResults.map((result) => (
          <Tooltip key={result.key} content={result.label} asChild>
            <Button onClick={result.onClick} leftIcon={result.leftIcon} className="justify-start">
              <span className="truncate">{result.label}</span>
            </Button>
          </Tooltip>
        ))}
    </div>
  );
};
